import { Directive, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { NgxSmartModalComponent } from 'ngx-smart-modal';
import { Subscription } from 'rxjs';

/**
 * Auto-attaches to every <ngx-smart-modal> element.
 * Measures the modal's "chrome" (title, subtitle, footer) heights
 * and sets a --modal-chrome-height CSS variable on .nsm-content so
 * .modal-content can use calc(86vh - var(--modal-chrome-height)) as
 * its max-height for reliable independent scrolling.
 *
 * This solves the fundamental CSS limitation where max-height on a
 * parent container doesn't give children a definite height for
 * overflow-y: auto to work against.
 */
@Directive({
    selector: 'ngx-smart-modal',
    standalone: false
})
export class ModalScrollDirective implements OnInit, OnDestroy {
    private resizeObserver: ResizeObserver | null = null;
    private nsmContent: HTMLElement | null = null;
    private subscriptions = new Subscription();

    constructor(
        private modal: NgxSmartModalComponent,
        private el: ElementRef
    ) {}

    ngOnInit(): void {
        this.subscriptions.add(
            this.modal.onOpen.subscribe(() => {
                requestAnimationFrame(() => this.setup());
            })
        );

        this.subscriptions.add(
            this.modal.onAnyCloseEventFinished.subscribe(() => {
                this.teardown();
            })
        );
    }

    private setup(): void {
        this.nsmContent = this.el.nativeElement.querySelector('.nsm-content');
        if (!this.nsmContent) return;

        this.updateChromeHeight();

        // Re-measure when chrome elements resize (e.g. title wraps on smaller viewport)
        this.resizeObserver = new ResizeObserver(() => this.updateChromeHeight());
        ['.modal-title', '.modal-subtitle', '.modal-action-line'].forEach(sel => {
            const child = this.nsmContent!.querySelector(sel);
            if (child) this.resizeObserver!.observe(child);
        });
    }

    private updateChromeHeight(): void {
        if (!this.nsmContent) return;

        const modalContent = this.nsmContent.querySelector('.modal-content') as HTMLElement;
        if (!modalContent) return;

        // Top chrome: distance from container top to content top.
        // Includes title, subtitle, and all margins between them.
        const containerRect = this.nsmContent.getBoundingClientRect();
        const contentRect = modalContent.getBoundingClientRect();
        const topChrome = contentRect.top - containerRect.top;

        // Bottom chrome: footer height including margins
        const footer = this.nsmContent.querySelector('.modal-action-line') as HTMLElement;
        let bottomChrome = 0;
        if (footer) {
            const footerStyle = getComputedStyle(footer);
            bottomChrome = footer.offsetHeight
                + parseFloat(footerStyle.marginTop)
                + parseFloat(footerStyle.marginBottom);
        }

        this.nsmContent.style.setProperty(
            '--modal-chrome-height',
            `${Math.ceil(topChrome + bottomChrome)}px`
        );
    }

    private teardown(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.nsmContent = null;
    }

    ngOnDestroy(): void {
        this.teardown();
        this.subscriptions.unsubscribe();
    }
}
